import React, { useEffect, useState } from 'react'
import { UserCompanyDto } from '../../services/api.types'
import { t } from 'ttag'
import { Redirect } from 'react-router-dom'
import { useHistory } from 'react-router'
import UserAdminWrapper from '../UserManagement/containers/UserAdmin/UserAdminWrapper'
import TeamAdminWrapper from '../TeamManagement/TeamManagementWrapper'
import AnnouncementsWrapper from '../Announcements/AnnouncementsWrapper'
import SsoAdminWrapper from '../SsoAdmin/SsoAdminWrapper'
import DomainAdminWrapper from '../DomainAdmin/DomainAdminWrapper'
import FeaturesAdminWrapper from '../FeaturesAdmin/FeaturesAdminWrapper'
import SignUpCodeAdminWrapper from '../SignUpCodeAdmin/SignUpCodeAdminWrapper'
import CompanyAdminWrapper from '../CompanyAdmin/CompanyAdminWrapper'
import CountriesAdminWrapper from '../CountriesAdmin/CountriesAdminWrapper'
import SuperAdminWrapper from '../SuperAdmin/SuperAdminWrapper'
import ImportUsersAdminWrapper from '../ImportUsersAdmin/ImportUsersAdminWrapper'
import ImportDataAdminWrapper from '../ImportDataAdmin/ImportDataAdminWrapper'
import { Box, Hidden, List, ListItem, ListItemText } from '@material-ui/core'
import makeStyles from '@material-ui/core/styles/makeStyles'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import LockIcon from '@material-ui/icons/Lock'
import { darkBlue, primaryBlue } from '../../styles/material-ui-theme-v2'
import { isRtl } from '../../i18nInit'
import YumLeaderboardTabsCompact from '../../components/YumLeaderboardTabs/YumLeaderboardTabsCompact'
import YumLeaderboardTabCompact from '../../components/YumLeaderboardTabs/YumLeaderboardTab/YumLeaderboardTabCompact'
import YumBlueTooltip from '../../components/YumBlueTooltip/YumBlueTooltip'
import { hasCompanyFeatureFlag } from '../../common/helpers/featureFlags'

export interface IAdminStateProps {
  tab: string | null
  userCompanies: UserCompanyDto[]
  managedCompanies: UserCompanyDto[]
  adminSelectedCompanyId: string | null
  isSuperAdmin: boolean
  superAdminTargetCompanyName: string | null
}

export interface IAdminDispatchProps {
  clearSuperAdminTargetCompany: () => void
}

export type IAdminProps = IAdminStateProps & IAdminDispatchProps

enum AdminSection {
  SUPER_ADMIN = 'super-admin',
  GENERAL = 'general',
  COUNTRIES = 'countries',
  USERS = 'users',
  GROUPS = 'groups',
  ANNOUNCEMENTS = 'announcements',
  SSO = 'sso',
  DOMAINS = 'domains',
  SIGNUP_CODES = 'signup-codes',
  IMPORT_USERS = 'import-users',
  IMPORT_DATA = 'import-data',
  FEATURES = 'features',
}

const getActiveSection = (tab: string | null): AdminSection => {
  switch (tab) {
    case 'super-admin':
      return AdminSection.SUPER_ADMIN
    case 'general':
      return AdminSection.GENERAL
    case 'countries':
      return AdminSection.COUNTRIES
    case 'groups':
      return AdminSection.GROUPS
    case 'announcements':
      return AdminSection.ANNOUNCEMENTS
    case 'sso':
      return AdminSection.SSO
    case 'domains':
      return AdminSection.DOMAINS
    case 'signup-codes':
      return AdminSection.SIGNUP_CODES
    case 'import-users':
      return AdminSection.IMPORT_USERS
    case 'import-data':
      return AdminSection.IMPORT_DATA
    case 'features':
      return AdminSection.FEATURES
    default:
      return AdminSection.USERS
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100%',
  },
  sidebar: {
    width: 180,
    flexShrink: 0,
  },
  sidebarTitle: {
    padding: '40px 16px 0 16px',
    marginBottom: '22px',
  },
  sidebarList: {
    padding: 0,
  },
  sidebarItem: {
    borderRadius: 10,
    margin: '2px 0',
    padding: '10px 16px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  sidebarItemActive: {
    backgroundColor: 'rgba(0, 0, 0, 0.06) !important',
    '& .MuiListItemText-primary': {
      color: primaryBlue,
      fontWeight: 600,
    },
  },
  sidebarItemText: {
    margin: 0,
    '& .MuiListItemText-primary': {
      fontSize: '0.875rem',
      lineHeight: '20px',
      color: darkBlue,
      fontWeight: 500,
      letterSpacing: '0.1px',
    },
  },
  disabledHint: {
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.4)',
    marginLeft: 8,
    verticalAlign: 'middle',
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    minWidth: 0,
    paddingLeft: isRtl ? 0 : 32,
    paddingRight: isRtl ? 32 : 0,
  },
  mobileContent: {
    minWidth: 0,
  },
}))

export default function Admin(props: IAdminProps) {
  const {
    tab,
    userCompanies,
    managedCompanies,
    adminSelectedCompanyId,
    isSuperAdmin,
    superAdminTargetCompanyName,
    clearSuperAdminTargetCompany,
  } = props
  const defaultCompanyId = managedCompanies.length > 0 ? managedCompanies[0].companyId : null
  const companyId = adminSelectedCompanyId || defaultCompanyId
  const generalAdmin = hasCompanyFeatureFlag(userCompanies, companyId, 'generalAdmin')
  const countriesAdmin = hasCompanyFeatureFlag(userCompanies, companyId, 'countriesAdmin')
  const announcementsAdmin = hasCompanyFeatureFlag(userCompanies, companyId, 'announcementsAdmin')
  const domainsAdmin = hasCompanyFeatureFlag(userCompanies, companyId, 'domainsAdmin')
  const ssoAdmin = hasCompanyFeatureFlag(userCompanies, companyId, 'ssoAdmin')
  const importUsersAdmin = hasCompanyFeatureFlag(userCompanies, companyId, 'importUsersAdmin')
  const importDataAdmin = hasCompanyFeatureFlag(userCompanies, companyId, 'importDataAdmin')
  const history = useHistory()
  const classes = useStyles()

  if (!tab) {
    return <Redirect to="/admin/users" />
  }

  const [activeSection, setActiveSection] = useState(getActiveSection(tab))

  useEffect(() => {
    setActiveSection(getActiveSection(tab))
  }, [tab])

  const navigate = (section: AdminSection) => {
    history.push(`/admin/${section}`)
  }

  const handleTabChange = (_event: React.ChangeEvent<{}>, section: AdminSection) => {
    navigate(section)
  }

  const menuItems: {
    section: AdminSection
    label: string
    visible: boolean
    disabledForAdmin: boolean
    superAdminOnly: boolean
  }[] = [
    {
      section: AdminSection.SUPER_ADMIN,
      label: t`Super admin`,
      visible: isSuperAdmin,
      disabledForAdmin: false,
      superAdminOnly: true,
    },
    {
      section: AdminSection.FEATURES,
      label: t`Features`,
      visible: isSuperAdmin,
      disabledForAdmin: false,
      superAdminOnly: true,
    },
    {
      section: AdminSection.GENERAL,
      label: t`General`,
      visible: generalAdmin || isSuperAdmin,
      disabledForAdmin: !generalAdmin,
      superAdminOnly: false,
    },
    { section: AdminSection.USERS, label: t`Users`, visible: true, disabledForAdmin: false, superAdminOnly: false },
    { section: AdminSection.GROUPS, label: t`Groups`, visible: true, disabledForAdmin: false, superAdminOnly: false },
    {
      section: AdminSection.COUNTRIES,
      label: t`Countries`,
      visible: countriesAdmin || isSuperAdmin,
      disabledForAdmin: !countriesAdmin,
      superAdminOnly: false,
    },
    {
      section: AdminSection.SIGNUP_CODES,
      label: t`Sign-up codes`,
      visible: true,
      disabledForAdmin: false,
      superAdminOnly: false,
    },
    {
      section: AdminSection.ANNOUNCEMENTS,
      label: t`Announcements`,
      visible: announcementsAdmin || isSuperAdmin,
      disabledForAdmin: !announcementsAdmin,
      superAdminOnly: false,
    },
    {
      section: AdminSection.DOMAINS,
      label: t`Domains`,
      visible: domainsAdmin || isSuperAdmin,
      disabledForAdmin: !domainsAdmin,
      superAdminOnly: false,
    },
    {
      section: AdminSection.SSO,
      label: t`Single sign-on`,
      visible: ssoAdmin || isSuperAdmin,
      disabledForAdmin: !ssoAdmin,
      superAdminOnly: false,
    },
    {
      section: AdminSection.IMPORT_USERS,
      label: t`Import users`,
      visible: importUsersAdmin || isSuperAdmin,
      disabledForAdmin: !importUsersAdmin,
      superAdminOnly: false,
    },
    {
      section: AdminSection.IMPORT_DATA,
      label: t`Import data`,
      visible: importDataAdmin || isSuperAdmin,
      disabledForAdmin: !importDataAdmin,
      superAdminOnly: false,
    },
  ]

  const visibleItems = menuItems.filter((item) => item.visible)

  // Redirect to first visible tab when current tab is hidden (e.g. after company switch)
  // Skip for sections that are accessible by URL only (features)
  useEffect(() => {
    if (activeSection === AdminSection.FEATURES || activeSection === AdminSection.SUPER_ADMIN) return
    const isCurrentTabVisible = visibleItems.some((item) => item.section === activeSection)
    if (!isCurrentTabVisible && visibleItems.length > 0) {
      navigate(visibleItems[0].section)
    }
  }, [companyId])

  const renderContent = () => {
    switch (activeSection) {
      case AdminSection.SUPER_ADMIN:
        return isSuperAdmin ? <SuperAdminWrapper /> : null
      case AdminSection.GENERAL:
        return generalAdmin || isSuperAdmin ? <CompanyAdminWrapper /> : null
      case AdminSection.COUNTRIES:
        return countriesAdmin || isSuperAdmin ? <CountriesAdminWrapper /> : null
      case AdminSection.USERS:
        return <UserAdminWrapper />
      case AdminSection.GROUPS:
        return <TeamAdminWrapper />
      case AdminSection.ANNOUNCEMENTS:
        return announcementsAdmin || isSuperAdmin ? <AnnouncementsWrapper /> : null
      case AdminSection.SSO:
        return ssoAdmin || isSuperAdmin ? <SsoAdminWrapper /> : null
      case AdminSection.DOMAINS:
        return domainsAdmin || isSuperAdmin ? <DomainAdminWrapper /> : null
      case AdminSection.SIGNUP_CODES:
        return <SignUpCodeAdminWrapper />
      case AdminSection.IMPORT_USERS:
        return importUsersAdmin || isSuperAdmin ? <ImportUsersAdminWrapper /> : null
      case AdminSection.IMPORT_DATA:
        return importDataAdmin || isSuperAdmin ? <ImportDataAdminWrapper /> : null
      case AdminSection.FEATURES:
        return <FeaturesAdminWrapper />
    }
    return null
  }

  return (
    <>
      {/* Desktop: sidebar + content */}
      <Hidden smDown>
        <Box className={classes.root}>
          <nav className={classes.sidebar}>
            <h1 className={classes.sidebarTitle}>{t`Admin`}</h1>
            {/* MUI List defaults to <ul>. Each ListItem is rendered as <a component="a">, which
              makes the <a> a direct child of <ul> — invalid list markup per WCAG 2.2 SC 1.3.1.
              Swap to <nav>-friendly <div> container to drop the implicit list semantics. */}
            <List component="div" role="presentation" className={classes.sidebarList}>
              {visibleItems.map((item) => (
                <ListItem
                  key={item.section}
                  button
                  component="a"
                  href={`/admin/${item.section}`}
                  className={`${classes.sidebarItem} ${activeSection === item.section ? classes.sidebarItemActive : ''}`}
                  onClick={(e: React.MouseEvent) => {
                    if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                      e.preventDefault()
                      navigate(item.section)
                    }
                  }}
                >
                  <ListItemText className={classes.sidebarItemText} primary={item.label} />
                  {item.superAdminOnly && (
                    <YumBlueTooltip title={t`Only visible to super admins`} arrow>
                      <LockIcon className={classes.disabledHint} />
                    </YumBlueTooltip>
                  )}
                  {item.disabledForAdmin && (
                    <YumBlueTooltip title={t`Disabled for admin — enable from Features`} arrow>
                      <VisibilityOffIcon className={classes.disabledHint} />
                    </YumBlueTooltip>
                  )}
                </ListItem>
              ))}
            </List>
          </nav>
          <div className={classes.content}>{renderContent()}</div>
        </Box>
      </Hidden>

      {/* Mobile: compact tabs + content */}
      <Hidden mdUp>
        <div>
          <YumLeaderboardTabsCompact onChange={handleTabChange} value={activeSection}>
            {visibleItems.map((item) => (
              <YumLeaderboardTabCompact
                key={item.section}
                label={
                  item.superAdminOnly ? (
                    <span className={classes.tabLabel}>
                      {item.label}
                      <LockIcon className={classes.disabledHint} />
                    </span>
                  ) : item.disabledForAdmin ? (
                    <span className={classes.tabLabel}>
                      {item.label}
                      <VisibilityOffIcon className={classes.disabledHint} />
                    </span>
                  ) : (
                    item.label
                  )
                }
                value={item.section}
              />
            ))}
          </YumLeaderboardTabsCompact>
          {/* No NotificationsWrapper here: every section view renders its own, so an Admin-level
            one would show the same notification twice. */}
          <div className={classes.mobileContent}>{renderContent()}</div>
        </div>
      </Hidden>
    </>
  )
}
